import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import Icon from '@mdi/react';
import { mdiCalendarMonth } from '@mdi/js';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteCardConfirmPopover from '../common/delete_card_confirm_popover';
import { MovableCardWrapper } from '../common/board/styles/Base';
import contentEntryPlaceholder from '../../assets/images/content_entry_placeholder.png';
import {
  useCreateContentEntryMutation,
  useDeleteContentEntryMutation,
  useGetDbPlannerWeekQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import EndUserRescheduleMealModal from '../meal/end_user_reschedule_meal_modal';
import { usePlannerViewWeekStartDate } from './planner_hooks';
import { useTracker } from '../../operations/tracking_operations';
import {
  dbWeekStartDateForViewWeekAndIndex,
  dbWeekStartDatesForDayIndexes,
  plannerWeekWithContentEntryAdded,
  plannerWeekWithObjectRemoved,
} from '../../services/planner';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { cloneObject } from '../../operations/utils';
import { deduplicate } from '../../services/arrays';
import {
  useGetContentEntryByIDAndParentQuery,
  useGetUserProfileQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { getOrCreatePlannerWeeks } from '../../operations/planner_operations';

const PlannerEntryContentEntryCard = ({
  id,
  title,
  style,
  className,
  onClick,
  onDelete,
}) => {
  const dispatch = useDispatch();

  const plannerViewWeekStartDate = usePlannerViewWeekStartDate();

  const dayIndex = style._dayIndex;

  const hasTitle = !!title;

  const contentEntryID = style._objectID;
  const parentID = style._parentID;

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: contentEntry } = useGetContentEntryByIDAndParentQuery(
    { id: contentEntryID, parentID },
    { skip: !contentEntryID || !parentID },
  );

  const plannerDbWeekStartDate = dbWeekStartDateForViewWeekAndIndex(
    plannerViewWeekStartDate,
    dayIndex,
  );

  const { data: dbPlannerWeek } = useGetDbPlannerWeekQuery(
    plannerDbWeekStartDate,
  );

  const [createContentEntry] = useCreateContentEntryMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const tracker = useTracker();

  const [deleteContentEntryAnchorEl, setDeleteContentEntryAnchorEl] =
    useState(null);

  const coverImageUrl = contentEntry?.coverImageUrl;
  const [rescheduleAnchorEl, setRescheduleAnchorEl] = useState(null);

  const setGlobalSnackbar = useCallback(
    (obj) => dispatch(showGlobalSnackbar(obj)),
    [dispatch],
  );

  const copyContentEntryToPlanner = useCallback(
    async (toPlannerViewWeekStartDate, dayIndexes) => {
      const dbWeekRecords = dbWeekStartDatesForDayIndexes(
        toPlannerViewWeekStartDate,
        dayIndexes,
      );
      const dbWeekStartDates = deduplicate(
        dbWeekRecords.map((dbwr) => dbwr.dbWeekStartDate),
      );

      const plannerWeeks = await getOrCreatePlannerWeeks(
        dbWeekStartDates,
        lazyGetOrCreateDbPlannerWeekByStartDate,
      );

      const insertedContentEntries = await Promise.all(
        dbWeekRecords.map(async (dbwr) => {
          const plannerWeek = plannerWeeks[dbwr.dbWeekStartDate];
          const input = { ...cloneObject(contentEntry) };
          input.parentID = plannerWeek.id;
          input.groups = null;
          const { data: createdContentEntry } = await createContentEntry(input);
          return createdContentEntry;
        }),
      );

      const updatedDbPlannerWeeks = await Promise.all(
        dbWeekRecords.map(async (dbwr, i) => {
          // plannerDbWeekStartDate: dbwr.dbWeekStartDate
          const updatedPlannerWeek = plannerWeekWithContentEntryAdded(
            dbPlannerWeek,
            insertedContentEntries[i].id,
            dbwr.dayIndex,
          );
          const { data: updatedDbPlannerWeek } = await updateDbPlannerWeek(
            updatedPlannerWeek,
          );
          return updatedDbPlannerWeek;
        }),
      );

      return updatedDbPlannerWeeks;
    },
    [
      contentEntry,
      createContentEntry,
      dbPlannerWeek,
      lazyGetOrCreateDbPlannerWeekByStartDate,
      updateDbPlannerWeek,
    ],
  );

  const onCopyToPlanner = useCallback(
    async (toPlannerViewWeekStartDate, dayIndexes) =>
      copyContentEntryToPlanner(toPlannerViewWeekStartDate, dayIndexes),
    [copyContentEntryToPlanner],
  );

  const onMove = useCallback(
    async (toPlannerViewWeekStartDate, toDayIndexes) => {
      const updatedDbPlannerWeeksAfterCopy = await copyContentEntryToPlanner(
        toPlannerViewWeekStartDate,
        toDayIndexes,
      );
      const updatedDbPlannerWeekAfterCopy =
        updatedDbPlannerWeeksAfterCopy[plannerDbWeekStartDate] || dbPlannerWeek;
      const updatedDbPlannerWeek = plannerWeekWithObjectRemoved(
        updatedDbPlannerWeekAfterCopy,
        id,
        dayIndex,
      );
      await Promise.all([
        deleteContentEntry({
          id,
          parentID,
        }),
        updateDbPlannerWeek(updatedDbPlannerWeek),
      ]);
    },
    [
      copyContentEntryToPlanner,
      dayIndex,
      dbPlannerWeek,
      deleteContentEntry,
      id,
      parentID,
      plannerDbWeekStartDate,
      updateDbPlannerWeek,
    ],
  );

  const handleClick = (ev) => {
    ev.stopPropagation();
    tracker(userProfile, [
      {
        name: 'Content card clicked',
        args: {
          title,
          id,
          hyperlink: null,
          section: 'Planner',
        },
      },
    ]);
    onClick(id);
  };

  if (!contentEntry) {
    console.warn(
      `Rendering PlannerEntryContentEntry card: content entry not found`,
    );
    return null;
  }

  const imgClassNames = classNames('plannerCardImage', {
    cardWithNoTitle: !hasTitle,
  });

  return (
    <MovableCardWrapper
      data-id={id}
      onClick={handleClick}
      style={style}
      className={className}>
      <Card sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex' }}>
          <CardMedia
            component="img"
            className={imgClassNames}
            image={coverImageUrl || contentEntryPlaceholder}
            title={title}
          />
          {hasTitle && (
            <CardContent
              title={title}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderBottom: '1px solid #7B7B7B',
              }}>
              <Typography variant="body2" component="p" sx={{ flexGrow: 1 }}>
                {title}
              </Typography>
            </CardContent>
          )}
        </Box>
        <CardActions sx={{ justifyContent: 'space-around' }}>
          <Tooltip title="Reschedule" placement="top-start">
            <IconButton
              aria-label="reschedule"
              size="small"
              onClick={(ev) => {
                setRescheduleAnchorEl(ev.target);
                ev.stopPropagation();
              }}>
              <Icon
                path={mdiCalendarMonth}
                style={{ width: '24px', height: '24px' }}
              />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label="delete"
            size="small"
            onClick={(ev) => {
              setDeleteContentEntryAnchorEl(ev.target);
              ev.stopPropagation();
            }}>
            <DeleteIcon />
          </IconButton>
          <DeleteCardConfirmPopover
            visible={!!deleteContentEntryAnchorEl}
            anchorEl={deleteContentEntryAnchorEl}
            title="Delete content entry"
            message="The content entry will be deleted permanently. Are you sure?"
            height={155}
            onConfirmDelete={() => onDelete(id)}
            onDismiss={() => setDeleteContentEntryAnchorEl(null)}
          />
        </CardActions>
      </Card>
      {rescheduleAnchorEl && (
        <EndUserRescheduleMealModal
          anchorEl={rescheduleAnchorEl}
          onCopyToPlanner={(toPlannerViewWeekStartDate, dayIndexes) => {
            onCopyToPlanner(toPlannerViewWeekStartDate, dayIndexes);
            setRescheduleAnchorEl(null);
            const snackbarParams = {
              notificationText: 'Your meal has been copied',
            };
            if (plannerViewWeekStartDate !== toPlannerViewWeekStartDate) {
              snackbarParams.linkText = 'View planner';
              snackbarParams.linkTarget = `/planner/${toPlannerViewWeekStartDate}`;
            }
            setGlobalSnackbar(snackbarParams);
          }}
          onMove={(toPlannerViewWeekStartDate, dayIndexes) => {
            onMove(toPlannerViewWeekStartDate, dayIndexes);
            setRescheduleAnchorEl(null);
            const snackbarParams = {
              notificationText: 'Your meal has been rescheduled',
            };
            if (plannerViewWeekStartDate !== toPlannerViewWeekStartDate) {
              snackbarParams.linkText = 'View planner';
              snackbarParams.linkTarget = `/planner/${toPlannerViewWeekStartDate}`;
            }
            setGlobalSnackbar(snackbarParams);
          }}
          onDismiss={() => setRescheduleAnchorEl(null)}
        />
      )}
    </MovableCardWrapper>
  );
};

PlannerEntryContentEntryCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object.isRequired,
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default PlannerEntryContentEntryCard;
